import React, { ReactElement, useState, useEffect } from "react";
import { auth, db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { WorkspaceMember } from "./useWorkspaceData";
import { AttachmentIcon } from "../components/AttachmentIcon";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { isDemoMode } from "../../../utils/demoMode";
import { Transaction, TimeFormat, DateFormat } from "../../../types";
import { convertTo12Hour } from "../../../utils/transactionUtils";

/**
 * Hook providing helper functions for Activity page
 * Includes formatting, icons, and user name utilities
 */
interface UseActivityHelpersProps {
  workspaceMembers: WorkspaceMember[];
  transactions?: Transaction[];
  workspaceOwnerId?: string | null;
  timeFormat?: TimeFormat; // "12h" or "24h", defaults to "24h"
  dateFormat?: DateFormat; // "MM/DD/YYYY" or "DD/MM/YYYY", defaults to "MM/DD/YYYY"
}

export const useActivityHelpers = ({ workspaceMembers, transactions = [], workspaceOwnerId = null, timeFormat = "24h", dateFormat = "MM/DD/YYYY" }: UseActivityHelpersProps) => {
  // Format date - respects workspace dateFormat setting (month-first vs day-first)
  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    if (dateFormat === "DD/MM/YYYY") {
      return `${day} ${month} ${year}`;
    }
    return `${month} ${day}, ${year}`;
  };

  // Format time from date - respects workspace timeFormat setting
  // Show placeholder if time is midnight (00:00:00)
  const formatTime = (date: Date | string | null | undefined): string => {
    if (!date) return timeFormat === "12h" ? "HH:MM AM" : "HH:MM";
    const d = date instanceof Date ? date : new Date(date);
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const seconds = d.getSeconds();
    
    // If time is exactly midnight (00:00:00), show placeholder
    if (hours === 0 && minutes === 0 && seconds === 0) {
      return timeFormat === "12h" ? "HH:MM AM" : "HH:MM";
    }
    
    // Format according to workspace setting
    if (timeFormat === "12h") {
      const { hour, minute, period } = convertTo12Hour(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`);
      return `${hour}:${minute} ${period}`;
    } else {
      // 24-hour format
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
  };

  // Get category icon
  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      "Food & Dining": "🍽️",
      Shopping: "🛍️",
      Transportation: "🚗",
      "Bills & Utilities": "💡",
      Entertainment: "🎬",
      Healthcare: "🏥",
      Education: "📚",
      Travel: "✈️",
      "Personal Care": "💅",
      "Gifts & Donations": "🎁",
      Salary: "💰",
      Freelance: "💼",
      Investment: "📈",
      Business: "🏢",
      Other: "📋",
    };
    return icons[category] || "📋";
  };

  // Get attachment icon component based on file type
  // Returns the AttachmentIcon component (wrapper function for backward compatibility)
  const getAttachmentIcon = (attachmentType: string | undefined, attachmentName: string | undefined): ReactElement => {
    return React.createElement(AttachmentIcon, { attachmentType, attachmentName });
  };

  // Cache for user names and photoURLs fetched from users collection (for users not in workspaceMembers)
  const [userNameCache, setUserNameCache] = useState<Record<string, string>>({});
  const [userPhotoURLCache, setUserPhotoURLCache] = useState<Record<string, string>>({});
  
  // Pre-fetch user names and photoURLs from users collection for users in transactions but not in workspaceMembers
  useEffect(() => {
    if (transactions.length === 0 && !workspaceOwnerId) return;
    
    // Get all unique user IDs from transactions
    const transactionUserIds = new Set(
      transactions
        .map(t => t.userId)
        .filter((userId): userId is string => Boolean(userId))
    );
    
    // IMPORTANT: Also include workspaceOwnerId if provided
    // This ensures the owner's name is fetched even if they haven't created transactions yet
    // or if they're in workspaceMembers but missing name/email
    if (workspaceOwnerId) {
      transactionUserIds.add(workspaceOwnerId);
    }
    
    // Find user IDs that are in transactions but not in workspaceMembers, OR
    // users in workspaceMembers but without name/email/photoURL (need to fetch from users collection)
    const workspaceMemberIds = new Set(workspaceMembers.map(u => u.id));
    const workspaceMembersWithoutData = new Set(
      workspaceMembers
        .filter(u => 
          u.id !== "bypass-user" && 
          u.id !== "demo-user" &&
          ((!u.name && !u.email) || !u.photoURL)
        )
        .map(u => u.id)
    );
    
    const userIdsToFetch = Array.from(transactionUserIds).filter(
      userId => 
        (!workspaceMemberIds.has(userId) || workspaceMembersWithoutData.has(userId)) && 
        userId !== "bypass-user" && 
        userId !== "demo-user"
    );
    
    if (userIdsToFetch.length === 0) return;
    
    // Fetch user documents for these IDs (only if not already in cache)
    // Check if we need to fetch name or photoURL for each user
    const userIdsToFetchNow = userIdsToFetch.filter(userId => {
      const user = workspaceMembers.find(u => u.id === userId);
      const needsName = !userNameCache[userId] && (!user || (!user.name && !user.email));
      const needsPhotoURL = !userPhotoURLCache[userId] && (!user || !user.photoURL);
      return needsName || needsPhotoURL;
    });
    if (userIdsToFetchNow.length === 0) return;
    
    // Fetch user documents for these IDs
    const fetchPromises = userIdsToFetchNow.map(async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fetchedName = (userData.name as string | undefined) || 
                             (userData.displayName as string | undefined) || 
                             (userData.email as string | undefined);
          const fetchedPhotoURL = userData.photoURL as string | undefined;
          
          let formattedName: string | undefined;
          if (fetchedName) {
            // Format if it's an email
            formattedName = fetchedName;
            if (fetchedName.includes("@")) {
              const emailPrefix = fetchedName.split("@")[0];
              formattedName = emailPrefix
                .split(/[._-]/)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(" ");
            }
          }
          
          return { 
            userId, 
            name: formattedName, 
            photoURL: fetchedPhotoURL 
          };
        }
      } catch (error) {
        // Silently handle errors - user data might not exist
      }
      return null;
    });
    
    Promise.all(fetchPromises).then((results) => {
      const nameUpdates: Record<string, string> = {};
      const photoURLUpdates: Record<string, string> = {};
      results.forEach((result) => {
        if (result) {
          if (result.name) {
            nameUpdates[result.userId] = result.name;
          }
          if (result.photoURL) {
            photoURLUpdates[result.userId] = result.photoURL;
          }
        }
      });
      if (Object.keys(nameUpdates).length > 0) {
        setUserNameCache(prev => ({ ...prev, ...nameUpdates }));
      }
      if (Object.keys(photoURLUpdates).length > 0) {
        setUserPhotoURLCache(prev => ({ ...prev, ...photoURLUpdates }));
      }
    });
    // Note: We intentionally don't include userNameCache and userPhotoURLCache in deps
    // to avoid infinite loops. The cache is checked inside the filter, and updates
    // will trigger a new effect run when transactions or workspaceMembers change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, workspaceMembers]);

  // Get user name by ID, with role always shown, and numbered if duplicates exist
  const getUserName = (userId: string): string => {
    if (!userId) {
      return "Unknown User";
    }
    
    // Bypass-user ID - special handling (matches Analytics)
    // Only return "Bypass-User" if the userId is actually "bypass-user"
    const BYPASS_USER_ID = "bypass-user";
    if (userId === BYPASS_USER_ID) {
      return "Bypass-User";
    }
    
    // IMPORTANT: In bypass mode, handle real owner IDs that don't match workspaceOwnerId
    // The workspace ownerId might be "bypass-user" but transactions created in full auth mode
    // have the real owner ID (e.g., "2PGtEhAvuWTOaY40RCGIY3FruBx1")
    // In this case, use the owner's name from workspaceMembers or cache
    if (isBypassAuthEnabled()) {
      // Check if userId matches workspaceOwnerId OR if it's a real Firebase user ID (not bypass-user/demo-user)
      const isRealOwnerId = userId !== BYPASS_USER_ID && 
                            userId !== "demo-user" && 
                            userId.length > 20 && // Real Firebase user IDs are 28 chars
                            !workspaceMembers.find(u => u.id === userId); // Not already in members
      
      // Also check if it matches workspaceOwnerId (in case workspace ownerId is the real ID)
      const matchesWorkspaceOwner = workspaceOwnerId && userId === workspaceOwnerId;
      
      if (matchesWorkspaceOwner || isRealOwnerId) {
        // Try to find owner in workspaceMembers by role (owner role)
        // In bypass mode, the owner might be in members with role="owner" even if ID doesn't match
        const owner = workspaceMembers.find((u) => u.role === "owner");
        if (owner) {
          let displayName = owner.name || owner.email;
          
          // If owner is in workspaceMembers but has no name/email, check cache
          if (!displayName && userNameCache[userId]) {
            displayName = userNameCache[userId];
          }
          
          // Format email if it's an email address
          if (displayName && displayName.includes("@")) {
            const emailPrefix = displayName.split("@")[0];
            displayName = emailPrefix
              .split(/[._-]/)
              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join(" ");
          }
          
          // If we have a name (from owner or cache), return it
          if (displayName) {
            return displayName;
          }
        }
        
        // If owner not found in workspaceMembers, try cache
        if (userNameCache[userId]) {
          let displayName = userNameCache[userId];
          // Format email if it's an email address
          if (displayName && displayName.includes("@")) {
            const emailPrefix = displayName.split("@")[0];
            displayName = emailPrefix
              .split(/[._-]/)
              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join(" ");
          }
          return displayName;
        }
      }
    }
    
    // Check cache FIRST for users fetched from users collection (for transaction creators not in workspaceMembers)
    // This should be checked early, before fallbacks, to show actual names
    // The cache is populated asynchronously when transactions load
    if (userNameCache[userId]) {
      return userNameCache[userId];
    }
    
    // First, check if user exists in workspaceMembers (this includes bypass-user and owner)
    const user = workspaceMembers.find((u) => u.id === userId);
    if (user) {
      // Use name, email, or check cache (for users in members but without name/email)
      let displayName = user.name || user.email;
      
      // If user is in workspaceMembers but has no name/email, check cache
      // This can happen when user document has name but member document doesn't
      if (!displayName && userNameCache[userId]) {
        displayName = userNameCache[userId];
      }
      
      // If still no name/email, use userId prefix as last resort (matches Analytics)
      // In demo mode, use "Demo User" instead
      if (!displayName) {
        displayName = isDemoMode() ? "Demo User" : `User ${userId.substring(0, 8)}`;
      }
      
      // Check if there are duplicate names (number them if needed)
      const usersWithSameName = workspaceMembers.filter(
        (u) => {
          const otherName = u.name || u.email || (isDemoMode() ? "Demo User" : `User ${u.id.substring(0, 8)}`);
          return otherName === displayName && u.id !== userId;
        }
      );
      
      // If there are duplicate names, number them
      if (usersWithSameName.length > 0) {
        const allUsersWithSameName = workspaceMembers.filter(
          (u) => {
            const otherName = u.name || u.email || (isDemoMode() ? "Demo User" : `User ${u.id.substring(0, 8)}`);
            return otherName === displayName;
          }
        );
        const sortedUsers = [...allUsersWithSameName].sort((a, b) => a.id.localeCompare(b.id));
        const userIndex = sortedUsers.findIndex((u) => u.id === userId);
        const userNumber = userIndex + 1;
        displayName = `${displayName} (${userNumber})`;
      }
      
      return displayName;
    }
    
    // Legacy user IDs - only map if they don't exist in workspaceMembers
    // These are old IDs that might not be in members list
    const LEGACY_USER_IDS = ["demo-user"]; // Removed dev-user-developer - we use bypass-user now
    
    // In bypass auth mode, map legacy IDs to workspace owner name (only if not found in members)
    if (isBypassAuthEnabled() && LEGACY_USER_IDS.includes(userId)) {
      // Find the owner in workspaceMembers
      const owner = workspaceMembers.find((u) => u.role === "owner");
      if (owner && owner.name) {
        return owner.name;
      }
      if (owner && owner.email) {
        return owner.email;
      }
      // If owner not found or has no name, fall through to normal handling
    }
    
    // Fallback: use auth user if it's the current user
    if (auth.currentUser && auth.currentUser.uid === userId) {
      return auth.currentUser.displayName || auth.currentUser.email || `User ${userId.substring(0, 8)}`;
    }
    
    // In demo mode, show "Demo User" instead of user IDs
    if (isDemoMode()) {
      return "Demo User";
    }
    
    // For real authenticated users not in workspaceMembers, the cache will be populated asynchronously
    // While waiting for cache, show userId prefix as temporary fallback
    // The cache should populate quickly after transactions load
    return `User ${userId.substring(0, 8)}`;
  };

  // Get user name in @username format for history display
  const getUserNameForHistory = (userId: string): string => {
    const user = workspaceMembers.find((u) => u.id === userId);
    if (user) {
      if (user.email) {
        const emailPrefix = user.email.split("@")[0];
        return `@${emailPrefix}`;
      }
      if (user.name) {
        return `@${user.name.toLowerCase().replace(/\s+/g, "")}`;
      }
    }
    
    // In bypass auth mode, if userId is not found, check if it might be an old workspaceOwnerId
    // In that case, try to find "bypass-user" member and use its name
    if (isBypassAuthEnabled()) {
      const bypassUser = workspaceMembers.find((u) => u.id === "bypass-user");
      if (bypassUser) {
        if (bypassUser.email) {
          const emailPrefix = bypassUser.email.split("@")[0];
          return `@${emailPrefix}`;
        }
        if (bypassUser.name) {
          return `@${bypassUser.name.toLowerCase().replace(/\s+/g, "")}`;
        }
      }
    }
    
    if (auth.currentUser && auth.currentUser.uid === userId) {
      if (auth.currentUser.email) {
        const emailPrefix = auth.currentUser.email.split("@")[0];
        return `@${emailPrefix}`;
      }
      if (auth.currentUser.displayName) {
        return `@${auth.currentUser.displayName.toLowerCase().replace(/\s+/g, "")}`;
      }
    }
    return "@unknown";
  };

  // Get user photoURL by ID
  const getUserPhotoURL = (userId: string): string | undefined => {
    const user = workspaceMembers.find((u) => u.id === userId);
    
    // First, check if user exists in workspaceMembers and has photoURL
    if (user && user.photoURL) {
      return user.photoURL;
    }
    
    // Check cache for photoURL (works for both users in members and not in members)
    // This can happen when user document has photoURL but member document doesn't
    if (userPhotoURLCache[userId]) {
      return userPhotoURLCache[userId];
    }
    
    // Fallback: if user not found in workspaceMembers, try to get from auth
    if (auth.currentUser && auth.currentUser.uid === userId) {
      return auth.currentUser.photoURL || undefined;
    }
    
    return undefined;
  };

  return {
    formatDate,
    formatTime,
    getCategoryIcon,
    getAttachmentIcon,
    getUserName,
    getUserNameForHistory,
    getUserPhotoURL,
    userNameCache, // Expose cache so Activity.tsx can use it to add users to workspaceMembers
    userPhotoURLCache, // Expose cache so Activity.tsx can use it to add users to workspaceMembers
  };
};
